import React, { useEffect, useState } from "react";
import TableDataLayer from "./TableDataLayer";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";

const emptyAddForm = () => ({
  name: "",
  url: "",
  clientDeviceId: "",
});

const DeviceLayer = () => {
  const [tableKey, setTableKey] = useState(0);
  const [alertAdd, setAlertAdd] = useState(null);
  const [alertEdit, setAlertEdit] = useState(null);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    url: "",
    clientDeviceId: "",
    status: "active",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const bumpTable = () => setTableKey((k) => k + 1);

  const fetchDevices = async (page = 1, pageSize = 10, search = "") => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(pageSize));
      const q = search != null ? String(search).trim() : "";
      if (q) params.set("search", q);

      const response = await apiCall({
        url: `devices?${params.toString()}`,
        method: "GET",
      });

      if (response.success && response.data) {
        const payload = response.data;
        return {
          data: payload.data || [],
          total: payload.total ?? 0,
        };
      }
      return { data: [], total: 0 };
    } catch (error) {
      console.error("Error fetching devices:", error);
      return { data: [], total: 0 };
    }
  };

  const openAddModal = () => {
    setAddForm(emptyAddForm());
    setAlertAdd(null);
  };

  const openEditModal = (row) => {
    setAlertEdit(null);
    setEditForm({
      id: row.id,
      name: row.name ?? "",
      url: row.url ?? "",
      clientDeviceId: row.clientDeviceId != null && row.clientDeviceId !== "" ? String(row.clientDeviceId) : "",
      status: row.status === "inactive" ? "inactive" : "active",
    });
  };

  const formatDate = (val) => {
    if (val == null || val === "") return "—";
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAlertAdd(null);
    try {
      const response = await apiCall({
        url: "devices",
        method: "POST",
        body: {
          name: addForm.name.trim() || null,
          url: addForm.url.trim(),
          clientDeviceId: addForm.clientDeviceId.trim() || null,
        },
      });
      if (response.success) {
        bumpTable();
        document.getElementById("device-modal-add-close")?.click();
        setAddForm(emptyAddForm());
      } else {
        setAlertAdd({
          message: response.message || "Could not create device",
          class: "danger",
        });
      }
    } catch (error) {
      const extra =
        error.payload && Array.isArray(error.payload.errors)
          ? error.payload.errors.map((x) => x.msg || x.message).filter(Boolean).join(" ")
          : "";
      setAlertAdd({
        message: extra ? `${error.message}. ${extra}` : error.message,
        class: "danger",
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setAlertEdit(null);
    try {
      const response = await apiCall({
        url: `devices/${editForm.id}`,
        method: "PUT",
        body: {
          name: editForm.name.trim() || null,
          url: editForm.url.trim(),
          clientDeviceId: editForm.clientDeviceId.trim() || null,
          status: editForm.status,
        },
      });
      if (response.success) {
        bumpTable();
        document.getElementById("device-modal-edit-close")?.click();
      } else {
        setAlertEdit({
          message: response.message || "Could not update device",
          class: "danger",
        });
      }
    } catch (error) {
      const extra =
        error.payload && Array.isArray(error.payload.errors)
          ? error.payload.errors.map((x) => x.msg || x.message).filter(Boolean).join(" ")
          : "";
      setAlertEdit({
        message: extra ? `${error.message}. ${extra}` : error.message,
        class: "danger",
      });
    }
  };

  const handleToggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      const response = await apiCall({
        url: `devices/${row.id}/status`,
        method: "PATCH",
        body: { status: next },
      });
      if (response.success) bumpTable();
    } catch (error) {
      console.error("Toggle status:", error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await apiCall({
        url: `devices/${deleteTarget.id}`,
        method: "DELETE",
      });
      if (response.success) {
        bumpTable();
        document.getElementById("device-modal-delete-close")?.click();
        setDeleteTarget(null);
      }
    } catch (error) {
      console.error("Delete device:", error);
    }
  };

  const columns = [
    {
      key: "id",
      label: "ID",
      render: (val) => (val != null && val !== "" ? String(val) : "—"),
    },
    { key: "name", label: "Name", render: (val) => val || "—" },
    {
      key: "clientDeviceId",
      label: "Client device ID",
      render: (val) => (
        <span
          title={val || ""}
          style={{ maxWidth: 200, display: "inline-block" }}
          className="text-truncate d-inline-block align-bottom font-monospace text-xs"
        >
          {val != null && val !== "" ? String(val) : "—"}
        </span>
      ),
    },
    {
      key: "url",
      label: "URL",
      render: (val) => (
        <span title={val} style={{ maxWidth: 280, display: "inline-block" }} className="text-truncate d-inline-block align-bottom">
          {val}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (val, row) => (
        <span
          className={`badge bg-${row.status === "active" ? "success-main" : "danger"}`}
        >
          {row.status === "active" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "lastSeen",
      label: "Last seen",
      render: (val) => formatDate(val),
    },
    {
      key: "updatedAt",
      label: "Updated",
      render: (val) => formatDate(val),
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <>
          <button
            type="button"
            title={row.status === "active" ? "Deactivate" : "Activate"}
            onClick={() => handleToggleStatus(row)}
            className="w-32-px h-32-px me-8 bg-warning-focus text-warning-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
          >
            <Icon icon={row.status === "active" ? "mingcute:pause-line" : "mingcute:play-line"} />
          </button>
          <button
            type="button"
            title="Edit"
            className="w-32-px h-32-px me-8 bg-success-focus text-success-600 rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            data-bs-toggle="modal"
            data-bs-target="#deviceModalEdit"
            onClick={() => openEditModal(row)}
          >
            <Icon icon="lucide:pencil" />
          </button>
          <button
            type="button"
            title="Delete"
            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center border-0"
            data-bs-toggle="modal"
            data-bs-target="#deviceModalDelete"
            onClick={() => setDeleteTarget(row)}
          >
            <Icon icon="mingcute:delete-2-line" />
          </button>
        </>
      ),
    },
  ];

  useEffect(() => {
    document.title = "SMS devices";
  }, []);

  return (
    <>
      <div className="card basic-data-table">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">SMS gateway devices</h5>
            <div>
              <button
                type="button"
                className="btn btn-success text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                data-bs-toggle="modal"
                data-bs-target="#deviceModalAdd"
                onClick={openAddModal}
              >
                <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                Add device
              </button>
            </div>
          </div>
        </div>
        <TableDataLayer
          columns={columns}
          totalRows={tableKey}
          fetchData={fetchDevices}
        />
      </div>

      {/* Add */}
      <div
        className="modal fade"
        id="deviceModalAdd"
        tabIndex={-1}
        aria-labelledby="deviceModalAddLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="deviceModalAddLabel">
                Add device
              </h1>
              <button
                type="button"
                id="device-modal-add-close"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              {alertAdd && (
                <div className={`alert alert-${alertAdd.class}`} style={{ fontSize: "12px" }}>
                  {alertAdd.message}
                </div>
              )}
              <form onSubmit={handleAddSubmit}>
                <p className="text-secondary-light text-sm mb-20">
                  The device is linked to your account.
                </p>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-add-name">
                    Name
                  </label>
                  <input
                    id="device-add-name"
                    className="form-control radius-8"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    placeholder="Optional label"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-add-url">
                    URL <span className="text-danger">*</span>
                  </label>
                  <input
                    id="device-add-url"
                    type="url"
                    className="form-control radius-8"
                    value={addForm.url}
                    onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                    placeholder="https://192.168.1.10:9090"
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-add-client-device-id">
                    Client device ID
                  </label>
                  <input
                    id="device-add-client-device-id"
                    className="form-control radius-8 font-monospace text-sm"
                    value={addForm.clientDeviceId}
                    onChange={(e) => setAddForm({ ...addForm, clientDeviceId: e.target.value })}
                    placeholder="Android ANDROID_ID (optional; for auto URL register)"
                    autoComplete="off"
                  />
                  <small className="text-secondary-light d-block mt-8">
                    If set, must match the phone’s device id when the app calls{" "}
                    <code className="text-xs">/api/gateway/register</code>.
                  </small>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit */}
      <div
        className="modal fade"
        id="deviceModalEdit"
        tabIndex={-1}
        aria-labelledby="deviceModalEditLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="deviceModalEditLabel">
                Edit device
              </h1>
              <button
                type="button"
                id="device-modal-edit-close"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              {alertEdit && (
                <div className={`alert alert-${alertEdit.class}`} style={{ fontSize: "12px" }}>
                  {alertEdit.message}
                </div>
              )}
              <form onSubmit={handleEditSubmit}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-edit-name">
                    Name
                  </label>
                  <input
                    id="device-edit-name"
                    className="form-control radius-8"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-edit-url">
                    URL <span className="text-danger">*</span>
                  </label>
                  <input
                    id="device-edit-url"
                    type="url"
                    className="form-control radius-8"
                    value={editForm.url}
                    onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-edit-client-device-id">
                    Client device ID
                  </label>
                  <input
                    id="device-edit-client-device-id"
                    className="form-control radius-8 font-monospace text-sm"
                    value={editForm.clientDeviceId}
                    onChange={(e) => setEditForm({ ...editForm, clientDeviceId: e.target.value })}
                    placeholder="Android ANDROID_ID (optional)"
                    autoComplete="off"
                  />
                  <small className="text-secondary-light d-block mt-8">
                    Used to pair this row with the physical gateway for automatic URL updates.
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="device-edit-status">
                    Status
                  </label>
                  <select
                    id="device-edit-status"
                    className="form-select radius-8"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete */}
      <div
        className="modal fade"
        id="deviceModalDelete"
        tabIndex={-1}
        aria-labelledby="deviceModalDeleteLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="deviceModalDeleteLabel">
                Delete device
              </h1>
              <button
                type="button"
                id="device-modal-delete-close"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <p className="mb-0">
                Delete{" "}
                <strong>{deleteTarget?.name?.trim() ? deleteTarget.name : "this device"}</strong>? This cannot be
                undone.
              </p>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceLayer;
